import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './modal';
import { Button } from './button';
import { useLanguage } from '../../context/LanguageContext';
import { useTenant } from '../../context/TenantContext';
import { useClient } from '../../context/ClientContext';
import apiService from '../../services/api.service';
import { saveCookiePreferences } from '../../utils/cookieManager';

/**
 * CookieSettingsModal
 * Allows users to manage their cookie preferences
 * Props:
 * - open (bool)
 * - onOpenChange (fn)
 */
const CookieSettingsModal = ({ open, onOpenChange }) => {
  const { t } = useLanguage();
  const { tenant } = useTenant();
  const { client, updateClient, isAuthenticated } = useClient();
  
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Load current preferences when modal opens
  useEffect(() => {
    if (open && tenant?.tenantid) {
      const storageKey = `cookieConsent_${tenant.tenantid}`;
      const raw = localStorage.getItem(storageKey);
      
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          setAnalyticsEnabled(parsed.analytics === true);
        } catch (error) {
          console.error('Failed to load cookie preferences:', error);
          setAnalyticsEnabled(false);
        }
      } else if (isAuthenticated && client?.cookiePreference) {
        try {
          const serverPrefs = JSON.parse(client.cookiePreference);
          setAnalyticsEnabled(serverPrefs.analytics === true);
        } catch (error) {
          console.error('Failed to parse server preferences:', error);
          setAnalyticsEnabled(false);
        }
      }
    }
  }, [open, tenant?.tenantid, client?.cookiePreference, isAuthenticated]);

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async () => {
    setIsLoading(true);

    try {
      const policies = {
        essential: true,
        analytics: analyticsEnabled
      };

      // Save to localStorage
      const saved = saveCookiePreferences(tenant?.tenantid, policies);
      
      if (!saved) {
        throw new Error('Failed to save preferences');
      }

      // Save to backend if logged in
      if (isAuthenticated && client?.clientId) {
        // Guard: skip API call if tenant context doesn't match authenticated user's tenant
        if (tenant?.tenantid && client?.tenantId && parseInt(tenant.tenantid) !== parseInt(client.tenantId)) {
          console.log('CookieSettingsModal - skipping API sync: tenant mismatch');
        } else {
          try {
            await apiService.post('/api/clients/cookiepoliciesaction', {
              tenantid: tenant?.tenantid,
              clientid: client?.clientId,
              policies
            });

            // Update client context
            if (updateClient) {
              const updatedPref = {
                ...policies,
                timestamp: new Date().toISOString()
              };
              updateClient({ cookiePreference: JSON.stringify(updatedPref) });
            }
          } catch (apiError) {
            console.error('Failed to sync preferences to server:', apiError);
            // Continue anyway - local save succeeded
          }
        }
      }

      // Trigger analytics update event
      window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { 
        detail: { essential: true, analytics: analyticsEnabled } 
      }));

      // Show success toast
      showToast(
        analyticsEnabled 
          ? t('cookieSettings.analyticsEnabled') || 'Analytics has been enabled'
          : t('cookieSettings.analyticsDisabled') || 'Analytics has been disabled'
      );

      // Close modal after short delay
      setTimeout(() => {
        onOpenChange(false);
      }, 1500);

    } catch (error) {
      console.error('Error saving cookie preferences:', error);
      showToast(t('cookieSettings.saveError') || 'Failed to save preferences');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[400px] max-w-[90vw] rounded-2xl px-4 py-5">
        <DialogHeader>
          <div className="flex items-center justify-between mb-2">
            <img 
              src={require('../../assets/images/icons/cookies-icon.png')} 
              alt={t('cookieSettings.title') || 'Cookie Settings'} 
              className="h-12 w-12" 
            />
          </div>
          <DialogTitle className="text-left text-lg font-bold text-theme-secondary">
            {t('cookieSettings.title') || 'Cookie Settings'}
          </DialogTitle>
          <DialogDescription className="text-left text-sm text-gray-600 mt-1">
            {t('cookieSettings.description') || 'Manage your cookie preferences'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Essential Cookies - Always On */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-theme-secondary mb-1">
                  {t('cookieSettings.essential.label') || 'Essential Cookies'}
                </h3>
                <p className="text-xs text-gray-600">
                  {t('cookieSettings.essential.description') || 'Required for the website to function. Cannot be disabled.'}
                </p>
              </div>
              <div className="ml-3">
                <span className="inline-block px-3 py-1 text-xs font-medium text-green-700 bg-green-100 rounded-full">
                  {t('cookieSettings.essential.alwaysOn') || 'Always On'}
                </span>
              </div>
            </div>
          </div>

          {/* Analytics Cookies - Toggle */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 id="analytics-label" className="text-sm font-semibold text-theme-secondary mb-1">
                  {t('cookieSettings.analytics.label') || 'Analytics Cookies'}
                </h3>
                <p id="analytics-desc" className="text-xs text-gray-600">
                  {t('cookieSettings.analytics.description') || 'Help us understand how you use our website to improve your experience.'}
                </p>
              </div>
              <div className="ml-3">
                <button
                  type="button"
                  role="switch"
                  aria-checked={analyticsEnabled}
                  aria-label={`${t('cookieSettings.analytics.label') || 'Analytics Cookies'}. ${analyticsEnabled ? (t('cookieSettings.analytics.on') || 'Currently enabled') : (t('cookieSettings.analytics.off') || 'Currently disabled')}`}
                  onClick={() => setAnalyticsEnabled(!analyticsEnabled)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setAnalyticsEnabled(!analyticsEnabled);
                    }
                  }}
                  tabIndex={0}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-theme-primary focus:ring-offset-2 ${
                    analyticsEnabled ? 'bg-theme-primary' : 'bg-gray-300'
                  }`}
                  disabled={isLoading}
                >
                  <span
                    aria-hidden="true"
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      analyticsEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
            <div className="mt-2">
              <span 
                id="analytics-status"
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className={`text-xs font-medium ${
                  analyticsEnabled ? 'text-green-600' : 'text-gray-500'
                }`}
              >
                {analyticsEnabled 
                  ? t('cookieSettings.analytics.on') || 'Enabled'
                  : t('cookieSettings.analytics.off') || 'Disabled'
                }
              </span>
            </div>
          </div>
        </div>

        {/* Toast Notification */}
        {toast && (
          <div 
            className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <p className="text-sm text-green-800 text-center">{toast}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <Button
            type="button"
            variant="secondary"
            size="lg"
            onClick={handleCancel}
            disabled={isLoading}
            tabIndex={0}
            className="flex-1 rounded-full focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
          >
            {t('cookieSettings.cancelButton') || 'Cancel'}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={handleSave}
            disabled={isLoading}
            tabIndex={0}
            className="flex-1 rounded-full focus:ring-2 focus:ring-theme-primary focus:ring-offset-2"
          >
            {isLoading 
              ? t('cookieSettings.savingButton') || 'Saving...'
              : t('cookieSettings.saveButton') || 'Save Preferences'
            }
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CookieSettingsModal;
